import { faMapPin } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PushErrorNotification, PushSuccessNotification, styles } from "@repo/helpers";
import { Button, ButtonTypes, Label, Separator } from "@repo/ui";
import { Building2, Copy, FlagTriangleRight, MapPin, Store, Trash2, User, X } from "lucide-react";

import { MapConfigurationController, useForkLayer } from "../../../api/mapConfigurationController";
import { useMapConfig } from "../config/mapConfigContext";
import { MapFilters } from "../filters/filters";
import { markExecuted } from "../hooks/queries/serverFilters";
import { createDefaultLayerFilters } from "./layer";
import { LAYER_COLORS, RAINBOW, RAINBOW_COLOR } from "./layerAppearance";
import { useMapLayers } from "./layerContext";
import { isPositionLayer } from "./layerTypes";
import style from "./style.module.scss";

const Pin = () => <FontAwesomeIcon icon={faMapPin} />;

const PINS = [
	{ id: "pin", icon: Pin, label: "Pin" },
	{ id: "flag", icon: FlagTriangleRight, label: "Flag" },
	{ id: "gpin", icon: MapPin, label: "GPin" },
	{ id: "person", icon: User, label: "Person" },
	{ id: "office", icon: Building2, label: "Office" },
	{ id: "store", icon: Store, label: "Store" },
];

export function LayerEditor({ layerId, onClose }: { layerId: string; onClose: () => void }) {
	const { layers, addLayer, updateLayer, removeLayer } = useMapLayers();
	const { currentConfig } = useMapConfig();
	const forkLayerMutation = useForkLayer();
	const layer = layers.get(layerId);

	if (!layer) return null;

	const colorOptions = isPositionLayer(layer.itemType) ? [...LAYER_COLORS, RAINBOW_COLOR] : LAYER_COLORS;
	const canCopy = currentConfig && !currentConfig.isReadonly && layer.layerId != null;
	const setAppearance = (appearance: { color?: string; icon?: string }) =>
		updateLayer(layerId, (current) => ({ ...current, ...appearance }));

	const handleCopyLayer = () => {
		if (!confirm("Are you sure you want to copy this layer?")) return;
		forkLayerMutation.mutate(
			{ configId: currentConfig.id, layerId: layer.layerId },
			{
				onSuccess: async (result) => {
					const forked = await MapConfigurationController.getById(result.id).catch(() => null);
					const copy = forked?.layers.find((l) => l.layerId === result.layerId);
					if (!copy) {
						PushErrorNotification("Layer copied, but it could not be loaded. Reload the map to see it.");
						return;
					}
					addLayer(markExecuted({ ...createDefaultLayerFilters(), ...copy }));
					PushSuccessNotification("Layer copied");
				},
			},
		);
	};

	return (
		<div className={style.layerSelector}>
			<div className={style.layerSelectorHeader}>
				<Label className={style.layerSelectorLabel}>{layer.name}</Label>
				<Button
					types={[ButtonTypes.Text]}
					size="small"
					className={style.layerSelectorCloseButton}
					onClick={onClose}
					title="Close layer"
				>
					<X className={style.layerSelectorCloseIcon} />
				</Button>
			</div>
			<div className={style.layerSelectorContent}>
				<MapFilters layerId={layerId} />

				<Separator />

				<div className={style.layerSelectorOptions}>
					<div className={style.layerSelectorOptionGroup}>
						<Label className={style.layerSelectorLabel}>Color</Label>
						<div className={style.layerSelectorOptionList}>
							{colorOptions.map((item) => {
								const isRainbow = item.value === RAINBOW;
								return (
									<button
										key={item.value}
										type="button"
										onClick={() => setAppearance({ color: item.value })}
										className={styles(
											style.layerSelectorOptionButton,
											layer.color === item.value && style.layerSelectorOptionButtonActive,
										)}
										title={
											isRainbow ? "Rainbow - Dynamic color based on unique identifier" : item.name
										}
									>
										<div
											className={styles(
												style.layerSelectorColorSwatch,
												isRainbow && style.rainbow,
											)}
											style={isRainbow ? {} : { backgroundColor: item.value }}
										/>
									</button>
								);
							})}
						</div>
						{layer.color !== RAINBOW && (
							<input
								id={`color-${layerId}`}
								type="color"
								value={layer.color || "#1f2937"}
								onChange={(e) => setAppearance({ color: e.target.value })}
								className={style.layerSelectorColorInput}
							/>
						)}
					</div>

					<div className={style.layerSelectorOptionGroup}>
						<Label className={style.layerSelectorLabel}>Icon</Label>
						<div className={style.layerSelectorOptionList}>
							{PINS.map(({ id, icon: Icon, label }) => (
								<button
									key={id}
									type="button"
									onClick={() => setAppearance({ icon: id })}
									className={styles(
										style.layerSelectorOptionButton,
										style.layerSelectorIconOptionButton,
										(layer.icon ?? "pin") === id && style.layerSelectorOptionButtonActive,
									)}
									title={label}
								>
									<Icon className={style.layerSelectorOptionIcon} />
								</button>
							))}
						</div>
					</div>
				</div>
			</div>
			<div className={style.layerSelectorActions}>
				{canCopy && (
					<Button
						types={[ButtonTypes.Outline]}
						onClick={handleCopyLayer}
						disabled={forkLayerMutation.isPending}
						fullWidth
						className="flex h-10 items-center justify-center"
					>
						<Copy className="mr-2 h-4 w-4" />
						Copy Layer
					</Button>
				)}
				<Button
					types={[ButtonTypes.Outline]}
					onClick={() => removeLayer(layerId)}
					fullWidth
					className={style.layerSelectorRemoveButton}
				>
					<Trash2 className={style.layerSelectorRemoveIcon} />
					Remove Layer
				</Button>
			</div>
		</div>
	);
}
